import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  useTheme,
  Chip,
  Tab,
  Tabs,
} from '@mui/material';
import { Add as AddIcon, Download as DownloadIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { DataTable } from '../../components/DataTable/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import { projectsAPI } from '../../services/api';
import { Project } from '../../types/api';
import { useSnackbar } from 'notistack';
import { ProjectCalendar } from '../../components/ProjectCalendar/ProjectCalendar';
import { api } from '../../services/api';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'completed':
      return 'info';
    case 'archived':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'active':
      return 'Aktiv';
    case 'completed':
      return 'Abgeschlossen';
    case 'archived':
      return 'Archiviert';
    default:
      return status;
  }
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

const columns = [
  { id: 'name', label: 'Projektname' },
  { id: 'client_name', label: 'Kunde' },
  { id: 'site_address', label: 'Adresse' },
  {
    id: 'contract_amount',
    label: 'Auftragssumme',
    format: (value: number) => value ? formatCurrency(value) : '0,00 €',
  },
  {
    id: 'material_share',
    label: 'Materialanteil',
    format: (value: number) => value ? formatCurrency(value) : '0,00 €',
  },
  {
    id: 'planned_hours',
    label: 'Geplante Stunden',
    format: (value: number) => value ? value.toString() : '0',
  },
  {
    id: 'start_date',
    label: 'Startdatum',
    format: (value: string) => value ? format(new Date(value), 'dd.MM.yyyy', { locale: de }) : '-',
  },
  {
    id: 'end_date',
    label: 'Enddatum',
    format: (value: string | null) =>
      value ? format(new Date(value), 'dd.MM.yyyy', { locale: de }) : '-',
  },
  {
    id: 'status',
    label: 'Status',
    format: (value: string) => (
      <Chip
        label={getStatusLabel(value)}
        color={getStatusColor(value) as any}
        size="small"
      />
    ),
  },
  {
    id: 'documents',
    label: 'Dokumente',
    format: (value: any[]) => value && value.length > 0 ? `${value.length} Dokument(e)` : 'Keine',
  },
];

export const ProjectsList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

  // Rolle-basierte Berechtigungen
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management';
  const isBauleiter = user?.role === 'bauleiter';
  const isForeman = user?.role === 'foreman';
  const isInstaller = user?.role === 'installer';
  
  // Admin, Management & Bauleiter können alles
  const canSeeAll = isAdmin || isManagement || isBauleiter;
  // Vorarbeiter & Monteure sehen nur eingeschränkte Daten
  const canSeeRestricted = isForeman || isInstaller;
  // Admin, Management & Bauleiter können Projekte erstellen
  const canCreateProjects = canSeeAll;
  // Admin & Management können alles bearbeiten, Bauleiter nur Auftragssumme
  const canEditProjects = canSeeAll;
  // Nur Admin, Management & Bauleiter können exportieren
  const canExportProjects = canSeeAll;
  
  // DEBUG: Rollen-Validierung ausgeben
  console.log('🔍 DEBUG - ProjectsList - User:', user);
  console.log('🔍 DEBUG - ProjectsList - User Role:', user?.role);
  console.log('🔍 DEBUG - ProjectsList - Permissions:', {
    isAdmin,
    isManagement,
    isBauleiter,
    isForeman,
    isInstaller,
    canCreateProjects,
    canEditProjects,
    canExportProjects
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getProjects();
      setProjects(response || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      enqueueSnackbar('Fehler beim Laden der Projekte', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/projects/new');
  };

  const handleEdit = (project: Project) => {
    navigate(`/projects/${project.id}/edit`);
  };

  const handleView = (project: Project) => {
    navigate(`/projects/${project.id}`);
  };

  const handleDelete = (project: Project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;

    try {
      await projectsAPI.deleteProject(projectToDelete.id);
      enqueueSnackbar('Projekt erfolgreich gelöscht', { variant: 'success' });
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      enqueueSnackbar('Fehler beim Löschen des Projekts', { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/projects/export', {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data as BlobPart]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `projekte_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('Export erfolgreich', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting projects:', error);
      enqueueSnackbar('Fehler beim Exportieren', { variant: 'error' });
    }
  };

  // Rolle-basierte Spalten für eingeschränkte Benutzer
  const restrictedColumns = columns.filter(col => 
    !['contract_amount', 'material_share'].includes(col.id) || canSeeAll
  );

  const currentColumns = canSeeAll ? columns : restrictedColumns;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1">
          Projekte
        </Typography>
        {canCreateProjects && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Neues Projekt
          </Button>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="project views">
          <Tab label="Liste" />
          <Tab label="Kalender" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Projektliste</Typography>
              {canExportProjects && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                >
                  Exportieren
                </Button>
              )}
            </Box>
            
            <DataTable
              rows={projects}
              columns={currentColumns}
              loading={loading}
              initialSortBy="start_date"
              initialOrder="desc"
              onView={handleView}
              {...(canEditProjects && { onEdit: handleEdit })}
              {...(canEditProjects && { onDelete: handleDelete })}
            />
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <ProjectCalendar  />
      </TabPanel>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Projekt löschen"
        message={`Möchten Sie das Projekt "${projectToDelete?.name}" wirklich löschen?`}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

// TabPanel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`project-tabpanel-${index}`}
    aria-labelledby={`project-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);
